import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { getUser } from "../../../helpers/user";
import { toast } from "sonner";
import {
  actionRegisterUsuario,
  actionUpdateUsuario,
} from "../../../features/usuarios-register/slices";
import { mapNiveisDeAcesso } from "../../../helpers/factory-fields";
import Select from "../../../components/Select";
import SelectLojas from "../../../components/DadosLojas/SelectLojas";
import SelectRegionais from "../../../components/DadosLojas/SelectRegionais";
import Input from "../../../components/Inputs";
import Button, { ButtonGroup } from "../../../components/Button";

interface IRegisterUserProps {
  user?: any;
  callback: () => void;
  cancelAction: () => void;
}

const RegisterUser = ({ user, callback, cancelAction }: IRegisterUserProps) => {
  const dispatch = useDispatch();
  const currentUser = getUser();

  const lojaSelected = useAppSelector((state) => state.getLojas.lojaSelected);
  const regionalSelected = useAppSelector(
    (state) => state.getRegionais.regionalSelected,
  );

  const regionais = useAppSelector(
    (state: any) => state.getRegionais.regionais,
  );

  const maskPhone = (value: string) => {
    if (!value) return "";

    const n = value.replace(/\D/g, "").substring(0, 11);

    if (n.length <= 10) {
      return n
        .replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3")
        .replace(/-$/, "");
    }
    return n.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").replace(/-$/, "");
  };

  const unmaskPhone = (value: string) => value.replace(/\D/g, "");

  const initialState = {
    nome: "",
    login: "",
    contato: "",
    email: "",
    nivel: "0",
    idLoja: "0",
    idRegional: "0",
    contatoAlerta: "",
    emailAlerta: "",
    idCliente: currentUser.usuario.idCliente,
    idsRegionaisVinculadas: [] as number[],
  };

  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        nome: user.nome || "",
        login: user.login || "",
        contato: maskPhone(user.contato || ""),
        email: user.email || "",
        nivel: String(user.nivel),
        idLoja: String(user.idLoja || "0"),
        idRegional: String(user.idRegional || "0"),
        contatoAlerta: maskPhone(user.contatoAlerta || ""),
        emailAlerta: user.emailAlerta || "",
        idCliente: user.idCliente || currentUser.usuario.idCliente,
        idsRegionaisVinculadas: user.idsRegionaisVinculadas || [],
      });
    }
  }, [user, currentUser.usuario.idCliente]);

  useEffect(() => {
    if (formData.nivel === "5" && lojaSelected) {
      setFormData((prev) => ({ ...prev, idLoja: String(lojaSelected) }));
    }
    if (formData.nivel === "6" && regionalSelected) {
      setFormData((prev) => ({
        ...prev,
        idRegional: String(regionalSelected),
      }));
    }
  }, [lojaSelected, regionalSelected, formData.nivel]);

  const handleToggleRegional = (id: number) => {
    setFormData((prev) => ({
      ...prev,
      idsRegionaisVinculadas: prev.idsRegionaisVinculadas.includes(id)
        ? prev.idsRegionaisVinculadas.filter((r) => r !== id)
        : [...prev.idsRegionaisVinculadas, id],
    }));
  };

  const isNivelCoordenador = formData.nivel === "9";

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    if (name === "nivel") {
      setFormData((prev) => ({
        ...prev,
        nivel: value,
        idLoja: "0",
        idRegional: "0",
        idsRegionaisVinculadas: [],
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    if (formData.nivel === "0") {
      toast.error("Por favor, selecione um nível de acesso.");
      return;
    }

    if (
      formData.nivel === "5" &&
      (formData.idLoja === "0" || !formData.idLoja)
    ) {
      toast.error("Usuário de loja individual requer a seleção de uma Loja.");
      return;
    }

    if (
      formData.nivel === "6" &&
      (formData.idRegional === "0" || !formData.idRegional)
    ) {
      toast.error("Usuário de acesso geral requer a seleção de uma Regional.");
      return;
    }

    if (
      formData.nivel === "9" &&
      formData.idsRegionaisVinculadas.length === 0
    ) {
      toast.error("Coordenador requer ao menos uma Regional.");
      return;
    }

    if (!formData.nome || !formData.login || !formData.email) {
      toast.error(
        "Preencha todos os campos obrigatórios (Nome, Login e Email).",
      );
      return;
    }

    setLoading(true);

    try {
      const finalPayload = {
        ...formData,
        contato: unmaskPhone(formData.contato),
        contatoAlerta: unmaskPhone(formData.contatoAlerta),
        idLoja: formData.nivel === "5" ? Number(formData.idLoja) : 0,
        idRegional: formData.nivel === "6" ? Number(formData.idRegional) : 0,
        idsRegionaisVinculadas:
          formData.nivel === "9" ? formData.idsRegionaisVinculadas : [],
        nivel: Number(formData.nivel),
        idCliente: Number(formData.idCliente),
      };

      if (user?.id) {
        await dispatch(
          actionUpdateUsuario({ ...finalPayload, id: user.id }),
        ).unwrap();
        toast.success("Usuário atualizado com sucesso!");
      } else {
        await dispatch(actionRegisterUsuario(finalPayload)).unwrap();
        toast.success("Usuário cadastrado com sucesso!");
      }

      callback();
    } catch (err: any) {
      const errorMessage = err?.message || "Erro ao salvar usuário.";

      const cleanMessage = errorMessage.replace("Erro - ", "");

      toast.error(cleanMessage);
      console.error("Erro detalhado:", err);
    } finally {
      setLoading(false);
    }
  };

  const isNivelLoja = formData.nivel === "5";
  const isNivelRegional = formData.nivel === "6";

  return (
    <div className="register-user-form">
      <section className="form-grid">
        <div className="full-column">
          <Select
            size="sm"
            label="Nível de Acesso"
            name="nivel"
            value={formData.nivel}
            onChange={handleChange}
            options={[
              { value: "0", children: "Selecione um nível..." },
              ...Object.keys(mapNiveisDeAcesso).map((key) => ({
                value: key,
                children: mapNiveisDeAcesso[key],
              })),
            ]}
          />
        </div>

        {isNivelLoja && (
          <div className="full-column fade-in">
            <SelectLojas idLoja={formData.idLoja} onChange={handleChange} />
          </div>
        )}

        {isNivelRegional && (
          <div className="full-column fade-in">
            <SelectRegionais
              idRegional={formData.idRegional}
              onChange={handleChange}
            />
          </div>
        )}

        {isNivelCoordenador && (
          <div className="full-column fade-in">
            <label className="label label--sm">Regionais que coordena *</label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 6,
              }}
            >
              {(regionais || []).map((r: any) => {
                const checked = formData.idsRegionaisVinculadas.includes(
                  Number(r.id),
                );
                return (
                  <label
                    key={r.id}
                    className={`ponto-item${checked ? " ponto-item--checked" : ""}`}
                    style={{ padding: "4px 10px" }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleRegional(Number(r.id))}
                      disabled={loading}
                    />
                    {r.nome}
                  </label>
                );
              })}
            </div>
            {formData.idsRegionaisVinculadas.length === 0 && (
              <p
                className="paragraph paragraph--sm"
                style={{ color: "#dc2626", marginTop: 6 }}
              >
                Selecione ao menos uma regional.
              </p>
            )}
          </div>
        )}

        <Input
          label="Nome Completo"
          name="nome"
          value={formData.nome}
          onChange={handleChange}
        />
        <Input
          label="Login (Matrícula)"
          name="login"
          value={formData.login}
          onChange={handleChange}
        />
        <Input
          label="E-mail"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
        />
        <Input
          label="Telefone (Contato)"
          name="contato"
          value={maskPhone(formData.contato)}
          onChange={handleChange}
        />

        <div className="divider">Configurações de Notificação (Alertas)</div>

        <Input
          label="E-mail para Alertas"
          name="emailAlerta"
          type="email"
          value={formData.emailAlerta}
          onChange={handleChange}
        />
        <Input
          label="Telefone para Alertas"
          name="contatoAlerta"
          value={maskPhone(formData.contatoAlerta)}
          onChange={handleChange}
        />
      </section>

      <footer className="mt-20">
        <ButtonGroup>
          <Button
            variant="secondary"
            size="sm"
            onClick={cancelAction}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={loading}
          >
            {user ? "Salvar Alterações" : "Cadastrar Usuário"}
          </Button>
        </ButtonGroup>
      </footer>
    </div>
  );
};

export default RegisterUser;
